import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';

const API_BASE = process.env.REACT_APP_API_URL || '';
const WS_URL = process.env.REACT_APP_WS_URL || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;

// --- Styles ---
const styles = {
  app: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    background: '#0f1117',
    color: '#e1e4e8',
    minHeight: '100vh',
    margin: 0,
  },
  header: {
    background: 'linear-gradient(135deg, #1a1f36 0%, #0d1025 100%)',
    borderBottom: '1px solid #2a2f45',
    padding: '20px 32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logo: {
    fontSize: '28px',
  },
  h1: {
    fontSize: '22px',
    fontWeight: 700,
    margin: 0,
    color: '#fff',
  },
  subtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: 0,
  },
  statusBadge: (connected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
    background: connected ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
    color: connected ? '#10b981' : '#ef4444',
    border: `1px solid ${connected ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
  }),
  dot: (connected) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: connected ? '#10b981' : '#ef4444',
  }),
  main: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '24px 32px',
    display: 'grid',
    gridTemplateColumns: '380px 1fr',
    gap: '24px',
  },
  panel: {
    background: '#161b2e',
    borderRadius: '12px',
    border: '1px solid #2a2f45',
    overflow: 'hidden',
  },
  panelHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid #2a2f45',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  panelTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#fff',
    margin: 0,
  },
  panelBody: {
    padding: '20px',
  },
  inputGroup: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  input: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #2a2f45',
    background: '#0f1117',
    color: '#e1e4e8',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  btnPrimary: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },
  btnDanger: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: 'none',
    background: 'rgba(239, 68, 68, 0.2)',
    color: '#ef4444',
    fontSize: '12px',
    cursor: 'pointer',
  },
  btnScrape: (running) => ({
    width: '100%',
    padding: '14px',
    borderRadius: '10px',
    border: 'none',
    background: running
      ? 'linear-gradient(135deg, #f59e0b, #d97706)'
      : 'linear-gradient(135deg, #10b981, #059669)',
    color: '#fff',
    fontSize: '16px',
    fontWeight: 700,
    cursor: running ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginTop: '16px',
    letterSpacing: '0.5px',
    boxShadow: running ? 'none' : '0 4px 15px rgba(16, 185, 129, 0.3)',
  }),
  keywordTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '8px',
    background: 'rgba(59, 130, 246, 0.15)',
    color: '#60a5fa',
    fontSize: '13px',
    fontWeight: 500,
    border: '1px solid rgba(59, 130, 246, 0.2)',
    margin: '0 6px 6px 0',
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    color: '#60a5fa',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '0 2px',
    opacity: 0.7,
  },
  logPanel: {
    maxHeight: '200px',
    overflowY: 'auto',
    padding: '12px',
    background: '#0a0d15',
    borderRadius: '8px',
    fontSize: '12px',
    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
    lineHeight: 1.7,
  },
  logEntry: (type) => ({
    color: type === 'error' ? '#ef4444'
      : type === 'success' ? '#10b981'
      : type === 'warning' ? '#f59e0b'
      : type === 'url' ? '#38bdf8'
      : '#6b7280',
    marginBottom: '2px',
    fontStyle: type === 'url' ? 'italic' : 'normal',
    fontSize: type === 'url' ? '11px' : '12px',
    wordBreak: type === 'url' ? 'break-all' : 'normal',
  }),
  resultCard: {
    background: '#1a1f36',
    borderRadius: '10px',
    border: '1px solid #2a2f45',
    padding: '16px 20px',
    marginBottom: '12px',
    transition: 'border-color 0.2s',
  },
  resultTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#fff',
    marginBottom: '8px',
  },
  resultMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '8px',
  },
  metaItem: {
    fontSize: '12px',
    color: '#9ca3af',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  resultDesc: {
    fontSize: '13px',
    color: '#6b7280',
    lineHeight: 1.5,
    marginBottom: '8px',
  },
  resultLink: {
    fontSize: '12px',
    color: '#3b82f6',
    textDecoration: 'none',
  },
  voucherBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    background: 'rgba(16, 185, 129, 0.15)',
    color: '#10b981',
    fontSize: '11px',
    fontWeight: 600,
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginBottom: '16px',
  },
  statCard: {
    background: '#0f1117',
    borderRadius: '10px',
    border: '1px solid #2a2f45',
    padding: '16px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#fff',
  },
  statLabel: {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '4px',
  },
  noResults: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#4b5563',
  },
  maxPagesInput: {
    width: '60px',
    padding: '10px 8px',
    borderRadius: '8px',
    border: '1px solid #2a2f45',
    background: '#0f1117',
    color: '#e1e4e8',
    fontSize: '14px',
    textAlign: 'center',
    outline: 'none',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    gap: '12px',
  },
  searchFilter: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #2a2f45',
    background: '#0f1117',
    color: '#e1e4e8',
    fontSize: '13px',
    outline: 'none',
  },
  exportBtn: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid #2a2f45',
    background: '#1a1f36',
    color: '#e1e4e8',
    fontSize: '13px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  foerderDropdown: {
    position: 'relative',
    marginTop: '16px',
  },
  foerderButton: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #2a2f45',
    background: '#0f1117',
    color: '#e1e4e8',
    fontSize: '13px',
    cursor: 'pointer',
    textAlign: 'left',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  foerderMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    maxHeight: '250px',
    overflowY: 'auto',
    background: '#1a1f36',
    border: '1px solid #2a2f45',
    borderRadius: '0 0 8px 8px',
    zIndex: 100,
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
  },
  foerderOption: (selected) => ({
    padding: '8px 14px',
    fontSize: '13px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: selected ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
    color: selected ? '#60a5fa' : '#e1e4e8',
    borderBottom: '1px solid #2a2f45',
    transition: 'background 0.15s',
  }),
  foerderCheckbox: (selected) => ({
    width: '16px',
    height: '16px',
    borderRadius: '4px',
    border: `2px solid ${selected ? '#3b82f6' : '#4b5563'}`,
    background: selected ? '#3b82f6' : 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: '10px',
    color: '#fff',
  }),
  foerderTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    borderRadius: '6px',
    background: 'rgba(16, 185, 129, 0.15)',
    color: '#10b981',
    fontSize: '11px',
    fontWeight: 500,
    border: '1px solid rgba(16, 185, 129, 0.2)',
    margin: '0 4px 4px 0',
  },
};

function App({ auth }) {
  // auth prop is passed from index.js (null when OIDC not configured)
  const isAuthEnabled = !!auth;
  const isAuthenticated = !isAuthEnabled || (auth && !auth.isLoading && auth.isAuthenticated);
  const isAuthLoading = isAuthEnabled && auth && auth.isLoading;

  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [maxPages, setMaxPages] = useState(1);
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [logs, setLogs] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [filter, setFilter] = useState('');
  const [stats, setStats] = useState({ total: 0, keywords: 0, voucher: 0 });
  const [foerderarten, setFoerderarten] = useState([]);
  const [selectedFoerderarten, setSelectedFoerderarten] = useState([]);
  const [foerderDropdownOpen, setFoerderDropdownOpen] = useState(false);
  const [bildungsanbieter, setBildungsanbieter] = useState('');
  const wsRef = useRef(null);
  const logRef = useRef(null);
  const foerderRef = useRef(null);

  // Helper: fetch with auth token
  const authFetch = useCallback((url, options = {}) => {
    const headers = { ...options.headers };
    if (auth && auth.user && auth.user.access_token) {
      headers['Authorization'] = `Bearer ${auth.user.access_token}`;
    }
    return fetch(url, { ...options, headers });
  }, [auth]);

  const addLog = useCallback((message, type = 'info') => {
    const time = new Date().toLocaleTimeString('de-DE');
    setLogs(prev => [...prev.slice(-100), { time, message, type }]);
  }, []);

  // Load config on mount
  useEffect(() => {
    if (!isAuthenticated) return;
    authFetch(`${API_BASE}/api/config`)
      .then(res => res.json())
      .then(cfg => {
        setKeywords(cfg.keywords || []);
        setMaxPages(cfg.maxPages || 1);
        addLog('Konfiguration geladen', 'success');
      })
      .catch(err => addLog(`Fehler beim Laden: ${err.message}`, 'error'));

    // Load Förderarten
    authFetch(`${API_BASE}/api/foerderarten`)
      .then(res => res.json())
      .then(data => {
        setFoerderarten(data || []);
        addLog(`${(data || []).length} Förderarten geladen`, 'success');
      })
      .catch(err => addLog(`Förderarten laden fehlgeschlagen: ${err.message}`, 'error'));
  }, [addLog, isAuthenticated, authFetch]);

  // Close Förderarten dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (foerderRef.current && !foerderRef.current.contains(e.target)) {
        setFoerderDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // WebSocket connection
  useEffect(() => {
    if (!isAuthenticated) return;
    const connect = () => {
      let wsUrl = WS_URL;
      if (auth && auth.user && auth.user.access_token) {
        wsUrl += (wsUrl.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(auth.user.access_token);
      }
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsConnected(true);
        addLog('WebSocket verbunden', 'success');
      };

      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        switch (msg.type) {
          case 'job_started':
            addLog(`Scraping gestartet: ${msg.payload.keywords.join(', ')}`, 'info');
            break;
          case 'keyword_started':
            addLog(`━━━ Keyword: "${msg.payload.keyword}" ━━━`, 'info');
            addLog(`→ Öffne ${msg.payload.url}`, 'url');
            break;
          case 'scraping_page':
            addLog(`Seite ${msg.payload.page} wird gescraped...`, 'info');
            addLog(`→ ${msg.payload.url}`, 'url');
            if (msg.payload.actualUrl && msg.payload.actualUrl !== msg.payload.url) {
              addLog(`↳ Weitergeleitet: ${msg.payload.actualUrl}`, 'url');
            }
            break;
          case 'page_scraped':
            addLog(`✓ Seite ${msg.payload.page}: ${msg.payload.count} Ergebnisse (${msg.payload.url})`, 'success');
            break;
          case 'page_error':
            addLog(`✗ Seite ${msg.payload.page} fehlgeschlagen: ${msg.payload.url}`, 'error');
            addLog(`  ${msg.payload.error}`, 'error');
            break;
          case 'keyword_completed':
            addLog(`"${msg.payload.keyword}": ${msg.payload.count} Treffer`, 'success');
            break;
          case 'keyword_error':
            addLog(`Fehler bei "${msg.payload.keyword}": ${msg.payload.error}`, 'error');
            break;
          case 'log':
            addLog(msg.payload.message, 'info');
            break;
          case 'job_completed':
            addLog(`Fertig! ${msg.payload.totalFound} Ergebnisse in ${Math.round(msg.payload.duration)}s`, 'success');
            setIsRunning(false);
            // Results are now stored before this message is sent, but add small delay as safety
            setTimeout(() => loadResults(), 300);
            break;
          default:
            break;
        }
      };

      ws.onclose = () => {
        setWsConnected(false);
        addLog('WebSocket getrennt, reconnect...', 'warning');
        setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();
    return () => wsRef.current?.close();
  }, [addLog, isAuthenticated, auth]);

  // Auto-scroll logs
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logs]);

  const loadResults = () => {
    authFetch(`${API_BASE}/api/results`)
      .then(res => res.json())
      .then(data => {
        const allCourses = (data || []).flatMap(job => job.results || []);
        setResults(allCourses);
        updateStats(allCourses);
      })
      .catch(err => addLog(`Fehler: ${err.message}`, 'error'));
  };

  const updateStats = (courses) => {
    const uniqueKeywords = new Set(courses.map(c => c.keyword));
    setStats({
      total: courses.length,
      keywords: uniqueKeywords.size,
      voucher: courses.filter(c => c.voucher).length,
    });
  };

  const addKeyword = () => {
    const kw = newKeyword.trim();
    if (!kw) return;
    authFetch(`${API_BASE}/api/config/keywords`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keyword: kw }),
    })
      .then(res => res.json())
      .then(cfg => {
        setKeywords(cfg.keywords || []);
        setNewKeyword('');
        addLog(`Keyword "${kw}" hinzugefuegt`, 'success');
      })
      .catch(err => addLog(`Fehler: ${err.message}`, 'error'));
  };

  const removeKeyword = (kw) => {
    authFetch(`${API_BASE}/api/config/keywords/${encodeURIComponent(kw)}`, { method: 'DELETE' })
      .then(res => res.json())
      .then(cfg => {
        setKeywords(cfg.keywords || []);
        addLog(`Keyword "${kw}" entfernt`, 'info');
      })
      .catch(err => addLog(`Fehler: ${err.message}`, 'error'));
  };

  const toggleFoerderart = (code) => {
    setSelectedFoerderarten(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const getSelectedFoerderartenLabels = () => {
    return selectedFoerderarten.map(code => {
      const f = foerderarten.find(fa => fa.code === code);
      return f ? f.label : code;
    });
  };

  const startScraping = () => {
    if (keywords.length === 0) {
      addLog('Bitte zuerst Keywords hinzufuegen!', 'warning');
      return;
    }
    setIsRunning(true);
    const foerderInfo = selectedFoerderarten.length > 0
      ? ` mit Förderarten: ${getSelectedFoerderartenLabels().join(', ')}`
      : '';
    const anbieterInfo = bildungsanbieter.trim()
      ? ` | Anbieter: "${bildungsanbieter.trim()}"`
      : '';
    addLog(`Starte Scraping${foerderInfo}${anbieterInfo}...`, 'info');

    authFetch(`${API_BASE}/api/scrape`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keywords, maxPages, foerderarten: selectedFoerderarten, bildungsanbieter: bildungsanbieter.trim() }),
    })
      .then(res => res.json())
      .then(data => addLog(`Job #${data.jobId} gestartet`, 'success'))
      .catch(err => {
        addLog(`Start fehlgeschlagen: ${err.message}`, 'error');
        setIsRunning(false);
      });
  };

  const clearResults = () => {
    authFetch(`${API_BASE}/api/results`, { method: 'DELETE' })
      .then(() => {
        setResults([]);
        setStats({ total: 0, keywords: 0, voucher: 0 });
        addLog('Ergebnisse geloescht', 'info');
      });
  };

  const exportCSV = () => {
    const filtered = getFilteredResults();
    if (filtered.length === 0) return;

    const headers = ['Nr.', 'Titel', 'Anbieter', 'Förderart', 'Weiterbildungsinhalte', 'Dauer', 'Format', 'Ort', 'Start', 'Bildungsgutschein', 'Keyword', 'URL'];
    const rows = filtered.map((r, idx) => [
      idx + 1, r.title, r.provider, r.foerderart || '', r.weiterbildungsinhalte || '',
      r.duration, r.format, r.location,
      r.startDate, r.voucher ? 'Ja' : 'Nein', r.keyword, r.url,
    ].map(v => `"${(String(v) || '').replace(/"/g, '""')}"`).join(','));

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meinnow-ergebnisse-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    addLog('CSV exportiert', 'success');
  };

  const exportExcel = () => {
    const filtered = getFilteredResults();
    if (filtered.length === 0) return;

    const headers = ['Nr.', 'Titel', 'Anbieter', 'Förderart', 'Weiterbildungsinhalte', 'Dauer', 'Format', 'Ort', 'Start', 'Bildungsgutschein', 'Keyword', 'URL'];
    const data = filtered.map((r, idx) => [
      idx + 1, r.title, r.provider, r.foerderart || '', r.weiterbildungsinhalte || '',
      r.duration, r.format, r.location,
      r.startDate, r.voucher ? 'Ja' : 'Nein', r.keyword, r.url,
    ]);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

    // Column widths
    ws['!cols'] = [
      { wch: 5 },   // Nr.
      { wch: 40 },  // Titel
      { wch: 30 },  // Anbieter
      { wch: 25 },  // Förderart
      { wch: 60 },  // Weiterbildungsinhalte
      { wch: 15 },  // Dauer
      { wch: 15 },  // Format
      { wch: 20 },  // Ort
      { wch: 15 },  // Start
      { wch: 18 },  // Bildungsgutschein
      { wch: 20 },  // Keyword
      { wch: 50 },  // URL
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ergebnisse');
    XLSX.writeFile(wb, `meinnow-ergebnisse-${new Date().toISOString().slice(0, 10)}.xlsx`);
    addLog('Excel exportiert', 'success');
  };

  const getFilteredResults = () => {
    if (!filter) return results;
    const f = filter.toLowerCase();
    return results.filter(r =>
      (r.title || '').toLowerCase().includes(f) ||
      (r.provider || '').toLowerCase().includes(f) ||
      (r.description || '').toLowerCase().includes(f) ||
      (r.keyword || '').toLowerCase().includes(f) ||
      (r.foerderart || '').toLowerCase().includes(f)
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') addKeyword();
  };

  const filteredResults = getFilteredResults();

  // Auth loading screen
  if (isAuthLoading) {
    return (
      <div style={{ ...styles.app, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'spin 1s linear infinite', display: 'inline-block' }}>&#x27F3;</div>
          <p style={{ fontSize: '18px', color: '#9ca3af' }}>Authentifizierung wird geladen...</p>
        </div>
      </div>
    );
  }

  // Login screen
  if (isAuthEnabled && !isAuthenticated) {
    return (
      <div style={{ ...styles.app, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center', background: '#161b2e', borderRadius: '16px', border: '1px solid #2a2f45', padding: '48px', maxWidth: '400px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>&#x1F510;</div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#fff', marginBottom: '8px' }}>meinNOW Scraper</h1>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '32px' }}>Bitte melden Sie sich an, um fortzufahren.</p>
          <button
            style={{ ...styles.btnPrimary, padding: '14px 32px', fontSize: '16px', width: '100%' }}
            onClick={() => auth.signinRedirect()}
          >
            Anmelden
          </button>
          {auth.error && (
            <p style={{ color: '#ef4444', fontSize: '13px', marginTop: '16px' }}>
              Fehler: {auth.error.message}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.app}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerTitle}>
          <span style={styles.logo}>&#x1F50D;</span>
          <div>
            <h1 style={styles.h1}>meinNOW Scraper</h1>
            <p style={styles.subtitle}>Weiterbildungssuche auf mein-now.de</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isAuthEnabled && auth.user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', color: '#9ca3af' }}>
                {auth.user.profile?.preferred_username || auth.user.profile?.email || 'User'}
              </span>
              <button
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #2a2f45', background: 'transparent', color: '#9ca3af', fontSize: '12px', cursor: 'pointer' }}
                onClick={() => auth.signoutRedirect()}
              >
                Abmelden
              </button>
            </div>
          )}
          <div style={styles.statusBadge(wsConnected)}>
            <span style={styles.dot(wsConnected)} />
            {wsConnected ? 'Verbunden' : 'Getrennt'}
          </div>
        </div>
      </header>

      {/* Main Grid */}
      <div style={styles.main}>
        {/* Left Sidebar */}
        <div>
          {/* Keywords Panel */}
          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <h2 style={styles.panelTitle}>Keywords</h2>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>{keywords.length} aktiv</span>
            </div>
            <div style={styles.panelBody}>
              <div style={styles.inputGroup}>
                <input
                  style={styles.input}
                  value={newKeyword}
                  onChange={e => setNewKeyword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Keyword eingeben..."
                />
                <button style={styles.btnPrimary} onClick={addKeyword}>+</button>
              </div>
              <div>
                {keywords.map(kw => (
                  <span key={kw} style={styles.keywordTag}>
                    {kw}
                    <button style={styles.removeBtn} onClick={() => removeKeyword(kw)}>✕</button>
                  </span>
                ))}
                {keywords.length === 0 && (
                  <p style={{ color: '#4b5563', fontSize: '13px', textAlign: 'center', margin: '12px 0' }}>
                    Noch keine Keywords
                  </p>
                )}
              </div>

              {/* Max Pages */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px' }}>
                <span style={{ fontSize: '13px', color: '#9ca3af' }}>Max. Seiten:</span>
                <input
                  type="number"
                  style={styles.maxPagesInput}
                  value={maxPages}
                  onChange={e => setMaxPages(Math.max(1, parseInt(e.target.value) || 1))}
                  min={1}
                  max={20}
                />
              </div>

              {/* Förderarten Dropdown */}
              <div style={styles.foerderDropdown} ref={foerderRef}>
                <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                  Förderarten:
                </div>
                <button
                  style={styles.foerderButton}
                  onClick={() => setFoerderDropdownOpen(!foerderDropdownOpen)}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedFoerderarten.length === 0
                      ? 'Alle (kein Filter)'
                      : `${selectedFoerderarten.length} ausgewählt`}
                  </span>
                  <span style={{ fontSize: '10px', transform: foerderDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
                </button>

                {foerderDropdownOpen && (
                  <div style={styles.foerderMenu}>
                    {foerderarten.map(fa => {
                      const selected = selectedFoerderarten.includes(fa.code);
                      return (
                        <div
                          key={fa.code}
                          style={styles.foerderOption(selected)}
                          onClick={() => toggleFoerderart(fa.code)}
                          onMouseEnter={e => { e.currentTarget.style.background = selected ? 'rgba(59, 130, 246, 0.25)' : 'rgba(255,255,255,0.05)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = selected ? 'rgba(59, 130, 246, 0.15)' : 'transparent'; }}
                        >
                          <div style={styles.foerderCheckbox(selected)}>
                            {selected && '✓'}
                          </div>
                          <span>{fa.label}</span>
                        </div>
                      );
                    })}
                    {foerderarten.length === 0 && (
                      <div style={{ padding: '12px', color: '#6b7280', fontSize: '12px', textAlign: 'center' }}>
                        Keine Förderarten verfügbar
                      </div>
                    )}
                  </div>
                )}

                {/* Selected tags */}
                {selectedFoerderarten.length > 0 && (
                  <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap' }}>
                    {selectedFoerderarten.map(code => {
                      const fa = foerderarten.find(f => f.code === code);
                      return (
                        <span key={code} style={styles.foerderTag}>
                          {fa ? fa.label : code}
                          <button
                            style={{ ...styles.removeBtn, color: '#10b981', fontSize: '12px' }}
                            onClick={() => toggleFoerderart(code)}
                          >✕</button>
                        </span>
                      );
                    })}
                    <button
                      style={{ ...styles.btnDanger, fontSize: '10px', padding: '2px 8px', marginBottom: '4px' }}
                      onClick={() => setSelectedFoerderarten([])}
                    >
                      Alle entfernen
                    </button>
                  </div>
                )}
              </div>

              {/* Bildungsanbieter Filter */}
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                  Bildungsanbieter:
                </div>
                <input
                  style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
                  value={bildungsanbieter}
                  onChange={e => setBildungsanbieter(e.target.value)}
                  placeholder="z.B. GFN GmbH (leer = alle)"
                />
                {bildungsanbieter.trim() && (
                  <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{
                      ...styles.foerderTag,
                      background: 'rgba(59, 130, 246, 0.15)',
                      color: '#60a5fa',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                    }}>
                      🏢 {bildungsanbieter.trim()}
                      <button
                        style={{ ...styles.removeBtn, color: '#60a5fa', fontSize: '12px' }}
                        onClick={() => setBildungsanbieter('')}
                      >✕</button>
                    </span>
                  </div>
                )}
              </div>

              {/* Scrape Button */}
              <button
                style={styles.btnScrape(isRunning)}
                onClick={startScraping}
                disabled={isRunning}
              >
                {isRunning ? (
                  <>
                    <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
                    Scraping läuft...
                  </>
                ) : (
                  <>🚀 Scraping starten</>
                )}
              </button>
            </div>
          </div>

          {/* Log Panel */}
          <div style={{ ...styles.panel, marginTop: '16px' }}>
            <div style={styles.panelHeader}>
              <h2 style={styles.panelTitle}>Live-Log</h2>
              <button
                style={{ ...styles.btnDanger, fontSize: '11px' }}
                onClick={() => setLogs([])}
              >
                Leeren
              </button>
            </div>
            <div style={{ padding: '12px' }}>
              <div style={styles.logPanel} ref={logRef}>
                {logs.map((log, i) => (
                  <div key={i} style={styles.logEntry(log.type)}>
                    <span style={{ opacity: 0.5 }}>[{log.time}]</span> {log.message}
                  </div>
                ))}
                {logs.length === 0 && (
                  <div style={{ color: '#374151', textAlign: 'center', padding: '20px' }}>
                    Warte auf Aktivität...
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Content */}
        <div>
          {/* Stats */}
          <div style={styles.statsRow}>
            <div style={styles.statCard}>
              <div style={{ ...styles.statValue, color: '#3b82f6' }}>{stats.total}</div>
              <div style={styles.statLabel}>Ergebnisse gesamt</div>
            </div>
            <div style={styles.statCard}>
              <div style={{ ...styles.statValue, color: '#8b5cf6' }}>{stats.keywords}</div>
              <div style={styles.statLabel}>Keywords durchsucht</div>
            </div>
            <div style={styles.statCard}>
              <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.voucher}</div>
              <div style={styles.statLabel}>Mit Bildungsgutschein</div>
            </div>
          </div>

          {/* Results Panel */}
          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <h2 style={styles.panelTitle}>Suchergebnisse</h2>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={styles.exportBtn} onClick={exportCSV}>📥 CSV</button>
                <button style={{ ...styles.exportBtn, background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', borderColor: 'rgba(16, 185, 129, 0.3)' }} onClick={exportExcel}>📊 Excel</button>
                <button style={{ ...styles.btnDanger, padding: '8px 12px' }} onClick={clearResults}>Leeren</button>
              </div>
            </div>
            <div style={styles.panelBody}>
              {/* Filter Bar */}
              <div style={styles.toolbar}>
                <input
                  style={styles.searchFilter}
                  placeholder="Ergebnisse filtern..."
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                />
                <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                  {filteredResults.length} von {results.length}
                </span>
              </div>

              {/* Result Cards */}
              <div style={{ maxHeight: 'calc(100vh - 340px)', overflowY: 'auto' }}>
                {filteredResults.map((r, i) => (
                  <div key={i} style={styles.resultCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={styles.resultTitle}>{r.title}</div>
                      {r.voucher && <span style={styles.voucherBadge}>Bildungsgutschein</span>}
                    </div>

                    <div style={styles.resultMeta}>
                      {r.provider && <span style={styles.metaItem}>🏢 {r.provider}</span>}
                      {r.foerderart && <span style={{ ...styles.metaItem, color: '#10b981', fontWeight: 600 }}>💰 {r.foerderart}</span>}
                      {r.duration && <span style={styles.metaItem}>⏱ {r.duration}</span>}
                      {r.format && <span style={styles.metaItem}>📋 {r.format}</span>}
                      {r.location && <span style={styles.metaItem}>📍 {r.location}</span>}
                      {r.startDate && <span style={styles.metaItem}>📅 {r.startDate}</span>}
                      <span style={{ ...styles.metaItem, color: '#60a5fa' }}>🏷 {r.keyword}</span>
                    </div>

                    {r.weiterbildungsinhalte && (
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#9ca3af', marginBottom: '4px' }}>
                          📚 Weiterbildungsinhalte:
                        </div>
                        <p style={{ ...styles.resultDesc, marginBottom: 0, whiteSpace: 'pre-line' }}>
                          {r.weiterbildungsinhalte.length > 300
                            ? r.weiterbildungsinhalte.slice(0, 300) + '...'
                            : r.weiterbildungsinhalte}
                        </p>
                      </div>
                    )}

                    {!r.weiterbildungsinhalte && r.description && (
                      <p style={styles.resultDesc}>
                        {r.description.length > 200
                          ? r.description.slice(0, 200) + '...'
                          : r.description}
                      </p>
                    )}

                    {r.url && (
                      <a href={r.url} target="_blank" rel="noopener noreferrer" style={styles.resultLink}>
                        Auf mein-now.de ansehen →
                      </a>
                    )}
                  </div>
                ))}

                {filteredResults.length === 0 && (
                  <div style={styles.noResults}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔎</div>
                    <p style={{ fontSize: '15px' }}>Noch keine Ergebnisse</p>
                    <p style={{ fontSize: '13px' }}>Keywords eingeben und Scraping starten</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Keyframes for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        * { box-sizing: border-box; }
        body { margin: 0; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #0f1117; }
        ::-webkit-scrollbar-thumb { background: #2a2f45; border-radius: 3px; }
        input:focus { border-color: #3b82f6 !important; }
      `}</style>
    </div>
  );
}

export default App;
